import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { IndicesRecord } from '@opensearch-project/opensearch/api/_types/cat.indices'

import { ButtonApp, Container, Spinner } from '@/components'
import { Param } from '@/models/params'

import { useCreateSellTreezIndex, useSellTreez, useUpdateSellTreezIndexMappings } from './hooks'
import {
  CreateAndEditIndexModal,
  SellHistoryTabContent,
  SellTreezDetailsTabContent,
  SellTreezIndexesTabContent,
  SellTreezInfraTabContent,
  SellTreezRawTabContent,
  SellTreezWorkflowsTabContent,
} from './components'
import { columnsIndexes } from './data/columnsIndexes'
import { IndexDetailsModal } from './components/IndexDetailsModal'
import { IndexFormData } from './data/types'
import { sanitizeIndexFormData } from './utils'

const tabListHeaders = ['Details', 'Infra', 'Indexes', 'Workflows', 'History', 'Raw']

export const ShowSellTreez = () => {
  const { sellTreezId } = useParams<Param>()
  const { sellTreez, isLoading } = useSellTreez(sellTreezId)
  const [selectedIndex, setSelectedIndex] = useState<IndicesRecord | undefined>(undefined)
  const [showModal, setShowModal] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  const onViewDetails = (index: IndicesRecord) => {
    setSelectedIndex(index)
    setShowDetails(true)
  }

  const onEditMapping = (index: IndicesRecord) => {
    setSelectedIndex(index)
    setShowModal(true)
  }

  const columnsIndexesDefs = columnsIndexes(onViewDetails, onEditMapping)

  const { createIndex, isCreating } = useCreateSellTreezIndex({
    sellTreezId: sellTreezId!,
    onSuccess: () => {
      setShowModal(false)
      setSelectedIndex(undefined)
    },
  })

  const { updateIndexMappings, isUpdating } = useUpdateSellTreezIndexMappings({
    sellTreezId: sellTreezId!,
    onSuccess: () => {
      setShowModal(false)
      setSelectedIndex(undefined)
    },
  })

  const handleSubmitIndex = async (data: IndexFormData) => {
    const sanitizedData = sanitizeIndexFormData(data)
    return selectedIndex ? updateIndexMappings(sanitizedData) : createIndex(sanitizedData)
  }

  return (
    <div className="flex flex-col gap-[20px] grow overflow-hidden">
      {isLoading && <Spinner />}
      <Container
        header={
          <div className="flex grow overflow-hidden xs:flex-col-reverse md:flex-row md:justify-between gap-[15px]">
            <p className="text-2xl font-bold">{sellTreez?.name}</p>
            <div className="flex justify-end">
              <ButtonApp
                isPrimary
                handleClick={() => {
                  setSelectedIndex(undefined)
                  setShowModal(true)
                }}
              >
                <div className="flex items-center gap-[10px]">
                  <PlusCircleIcon className="text-white h-[24px] w-[24px]" />
                  <p>Create an Index</p>
                </div>
              </ButtonApp>
            </div>
          </div>
        }
      >
        <TabGroup className="grow flex flex-col gap-6 max-w-full">
          <TabList className="flex gap-2 items-baseline border-b border-gray-100 px-8 mx-2">
            {tabListHeaders.map((header, index) => (
              <Tab
                key={index}
                className="-mb-[1px] focus-visible:outline-none py-2 px-4 text-gray-500 text-sm rounded-[4px] rounded-b-none border border-gray-100 bg-[#F5F7FA] data-[selected]:bg-white data-[selected]:border-b-white"
              >
                {header}
              </Tab>
            ))}
          </TabList>
          <TabPanels className="grow overflow-auto px-4">
            <TabPanel>
              <SellTreezDetailsTabContent sellTreez={sellTreez} />
            </TabPanel>
            <TabPanel>
              <SellTreezInfraTabContent sellTreez={sellTreez} />
            </TabPanel>
            <TabPanel>
              <SellTreezIndexesTabContent
                sellTreez={sellTreez}
                columnIndexDefs={columnsIndexesDefs}
              />
            </TabPanel>
            <TabPanel>
              <SellTreezWorkflowsTabContent />
            </TabPanel>
            <TabPanel>
              <SellHistoryTabContent />
            </TabPanel>
            <TabPanel>
              <SellTreezRawTabContent sellTreez={sellTreez} />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </Container>
      <CreateAndEditIndexModal
        isOpenModal={showModal}
        isSubmitting={isCreating || isUpdating}
        handleSubmitIndex={handleSubmitIndex}
        index={selectedIndex}
        handleCloseModal={() => {
          setShowModal(false)
          setSelectedIndex(undefined)
        }}
      />
      <IndexDetailsModal
        index={selectedIndex}
        isOpen={showDetails}
        handleCloseModal={() => {
          setShowDetails(false)
          setSelectedIndex(undefined)
        }}
      />
    </div>
  )
}
